"use client";

import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { useEffect, useState } from "react";
import Loading from "../../../components/Loading/index.js";
import { useParams, useSearchParams } from "react-router-dom";

import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { planPagos, roundUp } from "../../../utils/planPagos.js";
import TablePagos from "./table.js";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere.js";
import DownloadPDF from "./downloadPDF.js";
import Investor from "./investor.js";
import {
  collection,
  doc,
  getDocs,
  increment,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../FirebaseConfig.ts";
import { calcularDeudaRestante } from "./calcularDeudaRestante.js";
import useUpdateDoc from "../../../hooks/useUpdateDoc.js";
import ModalContainer from "../components/Table.js/ModalContainer.js";
import Modal from "../../../components/Modal/index.js";
import Abono from "./abono.js";
import ModalAbono from "./modalAbonos.js";
import { obtenerPagosRealizados } from "../../../utils/pagosRealizados.js";
// import { db } from "../../../FirebaseConfig.js";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrAfter);

function PlanDePagos() {
  const { identity, id, idSimulator } = useParams();

  const [currentRequest, setCurrentRequest] = useState({});
  const [searchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const investmentId = searchParams.get("i");
  const [fechaPagoAnterior, setFechaPagoAnterior] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [showDate, setshowDate] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [investor, setInvestor] = useState([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [abonoModal, setAbonoModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [modalAbonos, setModalAbonos] = useState(false);
  const [modalPago, setModalPago] = useState(false);
  const [totalAmountPaid, setTotalAmountPaid] = useState(0);

  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({
    ammount: 0,
    tasa: 0,
    start_date: "",
  });

  const { handleUpdate } = useUpdateDoc();

  const { data: request, isFetching: loading } = useSnapshotWhere(
    `${idSimulator ? "simulator" : "requests"}`,
    {
      filter: {
        firstCondition: "uuid",
        secondCondition: "==",
        thirdCondition: id ? id : idSimulator,
      },
    }
  );

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const data = request[0];

  useEffect(() => {
    (async () => {
      if (data?.investorId) {
        const q = query(
          collection(db, "investor"),
          where("uuid", "==", data?.investorId)
        );

        const subscriber = onSnapshot(q, {
          next: (snapshot) => {
            const data = [];
            snapshot.docs.forEach((doc) => {
              data.push({
                id: doc.id,
                ...doc.data(),
              });
            });

            setInvestor(data);
          },
        });

        return () => subscriber();
      }
    })();
  }, [data?.investorId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loading />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4 p-8">
          <div className="w-20 h-20 mx-auto bg-gray-200 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No hay datos disponibles
            </h3>
            <p className="text-gray-600">
              No se encontró información del crédito solicitado
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { schedule, totalCredit } = planPagos(data);
  const { schedule: scheduleInvestor } = planPagos(data, data?.tasaInvestor);

  // Filtramos los pagos realizados hasta el momento
  const pagosRealizados = obtenerPagosRealizados(schedule, data?.payments);

  // Sumamos los pagos realizados
  const totalPagado = pagosRealizados.reduce(
    (acc, { cuotaAPagar }) => acc + cuotaAPagar,
    0
  );

  const totalPagadoAbonos = data.abono?.reduce(
    (acc, { amount }) => acc + amount,
    0
  );

  const pagosRealizadosInvestor = obtenerPagosRealizados(
    schedule,
    data?.paymentsInvestor
  );

  // Calculamos la deuda restante
  const nextPayment = schedule[pagosRealizados.length] || schedule[0];
  const nextPaymentInvestor =
    scheduleInvestor[pagosRealizadosInvestor.length] || scheduleInvestor[0];

  const currentPayment = schedule[pagosRealizados.length - 1];

  const currentPaymentInvestor =
    scheduleInvestor[pagosRealizadosInvestor.length - 1];

  const abonos = Array.isArray(data?.abono) ? data.abono : [];
  const ultimoAbono = abonos[abonos.length - 1];

  const progressPercentage = (
    (pagosRealizados.length / schedule.length) *
    100
  ).toFixed(1);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const isPaymentForSofia = dayjs(data?.start_date).isSameOrAfter(
    dayjs("2025-10-28"),
    "day"
  );

  const handleFullPayment = async () => {
    setIsProcessingPayment(true);

    try {
      // Calcular deuda total hasta la fecha actual
      const deudaTotal = Number(
        calcularDeudaRestante(
          nextPayment,
          fechaPagoAnterior,
          currentPayment,
          Number(data.tasa)
        ).toFixed(0)
      );

      setTotalAmountPaid(deudaTotal);

      const montoLendy =
        deudaTotal -
        Number(
          calcularDeudaRestante(
            nextPaymentInvestor,
            fechaPagoAnterior,
            currentPaymentInvestor,
            Number(data?.tasaInvestor)
          ).toFixed(0)
        );

      const montoSofiaWithoutFormat = Number(montoLendy) * 0.01;
      const montoSofia = Number(montoSofiaWithoutFormat.toFixed(0));

      const investorCollection = collection(db, "investor");

      const q = query(
        investorCollection,
        where("uuid", "==", "gyorAaGrlQRBwA1jA6a4")
      );
      const dataInvestor = await getDocs(q);
      const resInvestorL = dataInvestor.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      const q2 = query(
        investorCollection,
        where("uuid", "==", "icQvajUVgZfA0BEuP26p")
      );
      const data2 = await getDocs(q2);
      const resInvestorS = data2.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));

      const lendyInvestor = resInvestorL[0];
      const sofiaInvestor = resInvestorS[0];

      await handleUpdate("requests", id, {
        status: "pagado",
        fechaPagoCompleto: fechaPagoAnterior,
        montoPagoCompleto: deudaTotal,
        montoPagoCompletoInvestor: Number(
          calcularDeudaRestante(
            nextPaymentInvestor,
            fechaPagoAnterior,
            currentPaymentInvestor,
            Number(data?.tasaInvestor)
          ).toFixed(0)
        ),
      });

      await handleUpdate("investor", investor[0].id, {
        currentAmmount:
          Number(investor[0].currentAmmount || 0) +
          Number(
            calcularDeudaRestante(
              nextPaymentInvestor,
              fechaPagoAnterior,
              currentPaymentInvestor,
              Number(data.tasaInvestor)
            ).toFixed(0)
          ),
      });

      //update income for lendy
      await handleUpdate("investor", lendyInvestor.uuid, {
        currentAmmount:
          Number(lendyInvestor?.currentAmmount || 0) + Number(montoLendy || 0),
      });

      //update income for sofia
      if (isPaymentForSofia) {
        await handleUpdate("investor", sofiaInvestor.uuid, {
          currentAmmount:
            Number(sofiaInvestor?.currentAmmount || 0) +
            Number(montoSofia || 0),
        });
      }

      setPaymentSuccess(true);
    } catch (error) {
      console.error("Error processing payment:", error);
      alert("Error al procesar el pago. Por favor intente nuevamente.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <>
      <Modal showModal={showModal} setShowModal={setShowModal}>
        <ModalContainer
          currentRequest={currentRequest}
          majorCompanyId={data?.majorCompanyId}
          setCurrentRequest={setCurrentRequest}
        />
      </Modal>
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header Section */}
          <div
            className={`transition-all duration-700 ease-out ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex flex-col lg:flex-row  lg:justify-between gap-6">
                <div className="space-y-2 w-[60%]">
                  <div className="flex justify-between w-full gap-3">
                    <div className="flex flex-row items-center justify-center w-full">
                      <div className="p-2 bg-orange-100 bg-white rounded-xl shadow-lg border border-gray-100 w-full">
                        <h1 className="text-3xl font-bold text-gray-900">
                          Plan de Pagos
                        </h1>
                        <p className="text-lg text-gray-600">
                          Crédito de {data?.name}
                        </p>
                        <div>
                          <button
                            onClick={() => {
                              setShowModal(true);
                              setCurrentRequest(data);
                            }}
                            className="bg-[#49b0a8] hover:bg-[#3a8a84] text-white rounded-lg p-1 mb-3 font-medium text-sm transition-colors duration-200"
                          >
                            Ver Solicitud
                          </button>
                        </div>
                        <div className="flex gap-3">
                          <DownloadPDF data={data} />
                          {data.status !== "pagado" ? (
                            <button
                              onClick={() => setShowPaymentModal(true)}
                              className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg transform hover:scale-105 text-sm"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              Pagar Completo
                            </button>
                          ) : (
                            ""
                          )}
                          <button
                            onClick={() => setAbonoModal(true)}
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg transform hover:scale-105 text-sm"
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Abono
                          </button>
                          {data?.investorId && (
                            <Investor
                              identity={identity}
                              id={id}
                              data={data}
                              idInvestor={data?.investorId}
                              investmentId={investmentId}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  {data?.status === "pagado" ? (
                    ""
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="mt-4 col-span-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>Progreso del crédito</span>
                          <span>{progressPercentage}% completado</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>
                            {pagosRealizados.length} de {schedule.length} pagos
                          </span>
                          <span>
                            {schedule.length - pagosRealizados.length} pagos
                            restantes
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="">
                  {/* Outstanding Debt Calculator */}
                  <div
                    className={`transition-all duration-700 ease-out delay-400`}
                  >
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                      {data?.status === "pagado" ? (
                        <div className="bg-primary/20 rounded-lg p-2">
                          <p>Este crédito ya fue pagado</p>
                          <div>
                            {data?.montoPagoCompleto && (
                              <>
                                {" "}
                                Monto: {formatCurrency(
                                  data?.montoPagoCompleto
                                )}{" "}
                              </>
                            )}
                          </div>
                          <div>
                            {" "}
                            {data?.fechaPagoCompleto && (
                              <>Fecha: {data?.fechaPagoCompleto}</>
                            )}{" "}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke="#1C274D"
                                strokeLinecap="round"
                                strokeWidth="1.5"
                                d="M21 12c0 4.714 0 7.071-1.318 8.535S16.242 22 12 22s-6.364 0-7.682-1.465C3 19.072 3 16.714 3 12s0-7.071 1.318-8.536S7.758 2 12 2s6.364 0 7.682 1.464c.876.974 1.17 2.343 1.268 4.536"
                              ></path>
                              <path
                                stroke="#1C274D"
                                strokeWidth="1.5"
                                d="M7 8c0-.465 0-.697.051-.888a1.5 1.5 0 0 1 1.06-1.06C8.304 6 8.536 6 9 6h6c.465 0 .697 0 .888.051a1.5 1.5 0 0 1 1.06 1.06C17 7.304 17 7.536 17 8s0 .697-.051.888a1.5 1.5 0 0 1-1.06 1.06C15.697 10 15.464 10 15 10H9c-.465 0-.697 0-.888-.051a1.5 1.5 0 0 1-1.06-1.06C7 8.696 7 8.464 7 8Z"
                              ></path>
                              <circle
                                cx="8"
                                cy="13"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="8"
                                cy="17"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="12"
                                cy="13"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="12"
                                cy="17"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="16"
                                cy="13"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                              <circle
                                cx="16"
                                cy="17"
                                r="1"
                                fill="#1C274D"
                              ></circle>
                            </svg>
                          </div>
                          <h2 className="text-xl font-semibold text-gray-900">
                            Calculadora de Deuda Pendiente
                          </h2>
                        </div>
                      )}

                      {!showDate ? (
                        <>
                          {data?.status === "pagado" ? (
                            ""
                          ) : (
                            <button
                              onClick={() => setshowDate(true)}
                              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:shadow-lg transform hover:scale-105"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke="#ffffff"
                                  strokeLinecap="round"
                                  strokeWidth="1.5"
                                  d="M21 12c0 4.714 0 7.071-1.318 8.535S16.242 22 12 22s-6.364 0-7.682-1.465C3 19.072 3 16.714 3 12s0-7.071 1.318-8.536S7.758 2 12 2s6.364 0 7.682 1.464c.876.974 1.17 2.343 1.268 4.536"
                                ></path>
                                <path
                                  stroke="#ffffff"
                                  strokeWidth="1.5"
                                  d="M7 8c0-.465 0-.697.051-.888a1.5 1.5 0 0 1 1.06-1.06C8.304 6 8.536 6 9 6h6c.465 0 .697 0 .888.051a1.5 1.5 0 0 1 1.06 1.06C17 7.304 17 7.536 17 8s0 .697-.051.888a1.5 1.5 0 0 1-1.06 1.06C15.697 10 15.464 10 15 10H9c-.465 0-.697 0-.888-.051a1.5 1.5 0 0 1-1.06-1.06C7 8.696 7 8.464 7 8Z"
                                ></path>
                                <circle
                                  cx="8"
                                  cy="13"
                                  r="1"
                                  fill="#ffffff"
                                ></circle>
                                <circle
                                  cx="8"
                                  cy="17"
                                  r="1"
                                  fill="#ffffff"
                                ></circle>
                                <circle
                                  cx="12"
                                  cy="13"
                                  r="1"
                                  fill="#ffffff"
                                ></circle>
                                <circle
                                  cx="12"
                                  cy="17"
                                  r="1"
                                  fill="#ffffff"
                                ></circle>
                                <circle
                                  cx="16"
                                  cy="13"
                                  r="1"
                                  fill="#ffffff"
                                ></circle>
                                <circle
                                  cx="16"
                                  cy="17"
                                  r="1"
                                  fill="#ffffff"
                                ></circle>
                              </svg>
                              Calcular Deuda Pendiente hasta la Fecha
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="space-y-6 w-full">
                          <div className="grid grid-cols-1 gap-6 items-end">
                            <div className="space-y-2">
                              <label
                                htmlFor="fechaPago"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Seleccionar fecha de cálculo
                              </label>
                              <div className="relative flex gap-3">
                                <input
                                  id="fechaPago"
                                  type="date"
                                  value={fechaPagoAnterior}
                                  onChange={(e) => {
                                    setFechaPagoAnterior(e.target.value);
                                    calcularDeudaRestante(
                                      e.target.value,
                                      fechaPagoAnterior,
                                      currentPayment,
                                      Number(data.tasa)
                                    );
                                  }}
                                  className="  px-4 py-3 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                />
                                <button
                                  onClick={() => setshowDate(false)}
                                  className="px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200"
                                >
                                  Ocultar Calculadora
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                                <svg
                                  className="w-6 h-6 text-orange-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                                  />
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Deuda Restante
                                </h3>
                                <p className="text-sm text-gray-600">
                                  Calculada al{" "}
                                  {dayjs(fechaPagoAnterior).format(
                                    "DD/MM/YYYY"
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="text-3xl font-bold text-orange-600">
                              {nextPayment
                                ? formatCurrency(
                                    roundUp(
                                      calcularDeudaRestante(
                                        nextPayment,
                                        fechaPagoAnterior,
                                        currentPayment,
                                        Number(data.tasa)
                                      )
                                    )
                                  )
                                : "Ingrese una fecha válida"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Left Column - Credit Info */}
            <div className="xl:col-span-2 space-y-6">
              {/* Credit Details Cards */}
              <div
                className={`transition-all duration-700 ease-out delay-200 ${
                  isLoaded
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Loan Details */}
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        Detalles del Préstamo
                      </h2>
                    </div>

                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600">
                          Valor del préstamo:
                        </span>
                        <div className="flex items-center gap-2">
                          {editingField === "ammount" ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={editValues.ammount}
                                onChange={(e) =>
                                  setEditValues({
                                    ...editValues,
                                    ammount: e.target.value,
                                  })
                                }
                                className="w-32 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button
                                onClick={async () => {
                                  const r = window.confirm(
                                    "¿Estás seguro de actualizar el valor de esta solicitud?"
                                  );
                                  if (!r) return;
                                  await handleUpdate("requests", id, {
                                    ammount: Number(editValues.ammount),
                                  });
                                  setEditingField(null);
                                }}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingField(null);
                                  setEditValues({
                                    ...editValues,
                                    ammount: data.ammount,
                                  });
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="font-semibold text-gray-900">
                                {formatCurrency(Number(data.ammount))}
                              </span>
                              <button
                                onClick={() => {
                                  setEditingField("ammount");
                                  setEditValues({
                                    ...editValues,
                                    ammount: data.ammount,
                                  });
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Tasa de interés */}
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600">
                          Tasa de interés anual:
                        </span>
                        <div className="flex items-center gap-2">
                          {editingField === "tasa" ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.1"
                                value={editValues.tasa}
                                onChange={(e) =>
                                  setEditValues({
                                    ...editValues,
                                    tasa: e.target.value,
                                  })
                                }
                                className="w-24 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <span className="text-sm">%</span>
                              <button
                                onClick={async () => {
                                  const r = window.confirm(
                                    "¿Estás seguro de actualizar la tasa de esta solicitud?"
                                  );
                                  if (!r) return;
                                  await handleUpdate("requests", id, {
                                    tasa: Number(editValues.tasa),
                                  });
                                  setEditingField(null);
                                }}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingField(null);
                                  setEditValues({
                                    ...editValues,
                                    tasa: data.tasa,
                                  });
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="font-semibold text-blue-600">
                                {data.tasa}%
                              </span>
                              <button
                                onClick={() => {
                                  setEditingField("tasa");
                                  setEditValues({
                                    ...editValues,
                                    tasa: data.tasa,
                                  });
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600">Periodo en meses:</span>
                        <div className="flex items-center gap-2">
                          {editingField === "month" ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.1"
                                value={editValues.month}
                                onChange={(e) =>
                                  setEditValues({
                                    ...editValues,
                                    month: e.target.value,
                                  })
                                }
                                className="w-24 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button
                                onClick={async () => {
                                  const r = window.confirm(
                                    "¿Estás seguro de actualizar los meses de esta solicitud?"
                                  );
                                  if (!r) return;
                                  await handleUpdate("requests", id, {
                                    month: Number(editValues.month),
                                  });
                                  setEditingField(null);
                                }}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingField(null);
                                  setEditValues({
                                    ...editValues,
                                    month: data.month,
                                  });
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="font-semibold text-blue-600">
                                {data.month}
                              </span>
                              <button
                                onClick={() => {
                                  setEditingField("month");
                                  setEditValues({
                                    ...editValues,
                                    month: data.month,
                                  });
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Fecha de inicio */}
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600">Fecha de inicio:</span>
                        <div className="flex items-center gap-2">
                          {editingField === "start_date" ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="date"
                                value={editValues.start_date}
                                onChange={(e) =>
                                  setEditValues({
                                    ...editValues,
                                    start_date: e.target.value,
                                  })
                                }
                                className="px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button
                                onClick={async () => {
                                  const r = window.confirm(
                                    "¿Estás seguro de actualizar la fecha de esta solicitud?"
                                  );
                                  if (!r) return;
                                  await handleUpdate("requests", id, {
                                    start_date: editValues.start_date,
                                  });
                                  setEditingField(null);
                                }}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingField(null);
                                  setEditValues({
                                    ...editValues,
                                    start_date: data.start_date,
                                  });
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="font-semibold text-gray-900">
                                {data.start_date}
                              </span>
                              <button
                                onClick={() => {
                                  setEditingField("start_date");
                                  setEditValues({
                                    ...editValues,
                                    start_date: data.start_date,
                                  });
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Summary */}
                  <div className="bg-primary rounded-xl shadow-lg p-4 text-white">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 11H7a2 2 0 00-2 2v7a2 2 0 002 2h2a2 2 0 002-2v-7a2 2 0 00-2-2zM13 7H11a2 2 0 00-2 2v11a2 2 0 002 2h2a2 2 0 002-2V9a2 2 0 00-2-2zM17 3H15a2 2 0 00-2 2v15a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2z"
                          />
                        </svg>
                      </div>
                      <h2 className="text-xl font-semibold">
                        Resumen de Pagos
                      </h2>
                    </div>

                    <div className="space-y-2">
                      <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                        <div className="text-sm opacity-90 mb-1">
                          Pago mensual
                        </div>
                        <div className="text-2xl font-bold">
                          {formatCurrency(schedule[0]?.cuotaAPagar)}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="text-sm opacity-90 mb-1">
                            Pagos realizados
                          </div>
                          <div className="text-xl font-bold">
                            {pagosRealizados.length}
                          </div>
                        </div>
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="text-sm opacity-90 mb-1">
                            Pagos realizados
                          </div>
                          <div className="text-xl font-bold">
                            {formatCurrency(totalPagado.toFixed(0))}
                          </div>
                        </div>
                        <div
                          className="bg-white/10 rounded-lg p-2 backdrop-blur-sm cursor-pointer"
                          onClick={() => {
                            setModalAbonos(true);
                          }}
                        >
                          <div className="text-sm opacity-90 mb-1 ">
                            Abonos{" "}
                            <button className="p-1 text-white hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                          </div>
                          <div className="text-xl font-bold">
                            {totalPagadoAbonos
                              ? formatCurrency(totalPagadoAbonos?.toFixed(0))
                              : 0}
                          </div>
                        </div>
                      </div>

                      {data?.status === "pagado" ? (
                        ""
                      ) : (
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="text-sm opacity-90 mb-1">
                            Deuda a fecha de corte
                          </div>
                          <div className="text-2xl font-bold">
                            {currentPayment?.cuota <
                            ultimoAbono?.startInstallment
                              ? formatCurrency(
                                  ultimoAbono?.ammount.toFixed(0) || 0
                                )
                              : formatCurrency(
                                  currentPayment?.saldoDeuda.toFixed(0) || 0
                                )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Schedule Table */}
          <div
            className={`transition-all duration-700 ease-out delay-600 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      Cronograma de Pagos
                    </h2>
                    <p className="text-sm text-gray-600">
                      Tabla detallada de amortización del crédito
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <TablePagos
                  loading={loading}
                  schedule={schedule}
                  payment={idSimulator ? false : true}
                  data={data}
                  investor={investor ? true : false}
                  investorData={investor?.[0]}
                  disabled={data?.status === "pagado" ? true : false}
                />
              </div>
            </div>
          </div>
        </div>
        {/* Payment Confirmation Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              {!paymentSuccess ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Confirmar Pago Completo
                    </h3>
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={isProcessingPayment}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <svg
                        className="w-6 h-6 text-yellow-600 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      <div>
                        <h4 className="font-medium text-yellow-900 mb-1">
                          ¿Estás seguro?
                        </h4>
                        <p className="text-sm text-yellow-800">
                          Esta acción marcará el crédito como pagado en su
                          totalidad y no se puede deshacer.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">
                      Detalles del Pago:
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cliente:</span>
                        <span className="font-medium">
                          {data.name} {data.lastName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monto a pagar:</span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(
                            calcularDeudaRestante(
                              nextPayment,
                              fechaPagoAnterior,
                              currentPayment,
                              Number(data.tasa)
                            )
                          )}
                        </span>
                      </div>
                      {data.tasaInvestor ? (
                        <div className="flex justify-between">
                          <span className="text-purple-600">
                            Monto a pagar del inversionista:
                          </span>
                          <span className="font-medium text-purple-600">
                            {formatCurrency(
                              calcularDeudaRestante(
                                nextPaymentInvestor,
                                fechaPagoAnterior,
                                currentPaymentInvestor,
                                Number(data.tasaInvestor)
                              )
                            )}
                          </span>
                        </div>
                      ) : (
                        ""
                      )}

                      <div className="flex justify-between">
                        <span className="text-gray-600">Fecha de pago:</span>

                        <div className="relative flex gap-3">
                          <input
                            id="fechaPago"
                            type="date"
                            value={fechaPagoAnterior}
                            onChange={(e) => {
                              setFechaPagoAnterior(e.target.value);
                            }}
                            className="  px-4 py-3 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                          />
                        </div>
                      </div>
                      {data.investorId && investor.length > 0 && (
                        <div className="pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500">
                            * El monto se acreditará automáticamente al
                            inversionista
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      disabled={isProcessingPayment}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleFullPayment}
                      disabled={isProcessingPayment}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isProcessingPayment ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Procesando...</span>
                        </>
                      ) : (
                        <>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span>Confirmar Pago</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      ¡Pago Exitoso!
                    </h3>
                    <p className="text-gray-600 mb-4">
                      El crédito ha sido marcado como pagado en su totalidad
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-800 mb-1">
                        Monto pagado:
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(totalAmountPaid)}
                      </p>
                    </div>
                    {data.investorId && investor.length > 0 && (
                      <p className="text-sm text-gray-600">
                        El monto ha sido acreditado al inversionista
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPaymentSuccess(false);
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all"
                  >
                    Cerrar
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Abono Modal */}
        <Abono
          abonoModal={abonoModal}
          setAbonoModal={setAbonoModal}
          data={data}
          deuda={Number(
            calcularDeudaRestante(
              nextPayment,
              fechaPagoAnterior,
              currentPayment,
              Number(data.tasa)
            ).toFixed(0)
          )}
          cuotaFija={schedule[0]?.cuotaAPagar}
          investor={investor?.[0]}
          date={fechaPagoAnterior}
        />
        <ModalAbono
          modalAbonos={modalAbonos}
          setModalAbonos={setModalAbonos}
          data={data?.abono}
          requestId={data.uuid}
          investor={investor?.[0]}
        />
      </div>
    </>
  );
}

export default PlanDePagos;
